/**
 * 📊 XLSX CONTROLLER
 * Location: src/registered/common/parsers/XlsxController.js
 */
import * as XLSX from 'xlsx';
import extractPresentation from "../../site-admin/components/Workflow-esg/presentationExtractor";
import extractDimensions from "../../site-admin/components/Workflow-esg/definitionExtractor";
import extractCalculations from "../../site-admin/components/Workflow-esg/calculationExtractor";
import buildSchema from "../../site-admin/components/Workflow-esg/schemaBuilder";

/**
* 📊 XLSX CONTROLLER (Streamlined Discovery Mode)
*/
export const XlsxController = {
    execute: async (buffer, onProgress) => {
        console.log("🚀 [CONTROLLER] Execution Started.");
        try {
            const workbook = XLSX.read(new Uint8Array(buffer), { type: 'array' });
            const sheetNames = workbook.SheetNames;
            
            console.log(`📡 DISCOVERED EXCEL: ${sheetNames.length} WORKSHEETS`);

            // Global store for the Trawler and UI
            const allWorksheets = [];

            const getSheetPayload = (name) => {
                const sheet = workbook.Sheets[name];
                if (!sheet) {
                    console.error(`❌ [CONTROLLER] Sheet NOT FOUND: "${name}"`);
                    return null;
                }

                // 🎯 Get raw data as Array of Arrays for the Trawler
                const rawRows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" });
                const headers = rawRows[0] || [];
                const dataRows = rawRows.slice(1);

                // 📝 REQUESTED STREAMLINED LOGGING
                console.log(`📂 Worksheet: "${name}"`);
                console.log(`   - Found ${headers.length} columns`);
                console.log(`   - Found ${dataRows.length} rows`);
                
                const colMap = {};
                headers.forEach((text, index) => {
                    const cleanName = String(text || "").trim();
                    if (cleanName) {
                        colMap[cleanName] = index;
                        console.log(`   - ${name}.${cleanName}`);
                    }
                });

                // Store for the Physical Trawl (Pass 1)
                allWorksheets.push({
                    name,
                    headers,
                    data: dataRows,
                    rowCount: dataRows.length
                });

                return { rows: rawRows, colMap };
            };

            // --- 🎯 STEP 1: DEFINITION (THE ANCHOR) ---
            const defData = getSheetPayload("DefinitionLinkbase");
            if (!defData) throw new Error("DefinitionLinkbase Missing - Aborting.");

            console.log("⚙️ [CONTROLLER] Launching Definition Extractor...");
            const dimensions = extractDimensions(defData.rows, defData.colMap);
            console.log(`✅ DEFINITION LINKBASE: ${dimensions.length} AXIS-MEMBER PAIRS`);

            // --- 🎯 STEP 2: PRESENTATION ---
            const presData = getSheetPayload("PresentationLinkbase");
            if (!presData) throw new Error("PresentationLinkbase Missing - Aborting.");

            console.log("🌲 [CONTROLLER] Launching Presentation Extractor...");
            const presentation = extractPresentation(presData.rows, presData.colMap);
            console.log(`✨ [CONTROLLER] Presentation Extraction Complete. Roots: ${presentation.roots.length}`);

            // --- 🎯 STEP 3: CALCULATION ---
            const calcData = getSheetPayload("CalculationLinkbase");
            let calculations = [];
            if (calcData) {
                console.log("🧮 [CONTROLLER] Launching Calculation Extractor...");
                calculations = extractCalculations(calcData.rows, calcData.colMap);
                console.log(`✅ CALCULATION LINKBASE: ${calculations.length} RULES`);
            }

            // Map all remaining sheets to ensure discovery covers everything
            sheetNames.forEach(name => {
                if (!["DefinitionLinkbase", "PresentationLinkbase", "CalculationLinkbase"].includes(name)) {
                    getSheetPayload(name);
                }
            });

            const esgTree = buildSchema(presentation, dimensions, calculations);
            console.log("🏁 [CONTROLLER] Schema Build Complete. Final Object Ready.");

            // 🏆 Return unified results for the Trawler and RTF services
            return { 
                format: 'XLSX_TAXONOMY', 
                esgTree, 
                worksheets: allWorksheets,
                calculationRecords: calculations,
                definitionPairs: dimensions
            };

        } catch (err) {
            console.error("💀 [CONTROLLER] FATAL ERROR:", err.message);
            if (onProgress) onProgress({ status: 'FAIL', label: err.message });
            throw err;
        }
    }
};